import { isNil } from 'ramda'

import type { QuestionAnswerInput } from '@healthblocks-io/core'
import type { IQuestionnaireWithHealthParameters, QuestionnaireQuestionWithHealthParameters } from 'index'
import type { HealthParameter, HealthParameterObservation } from '@/modules/app/types/HealthParameter.types'

/**
 * Builds a map from anchorLinkId to its dependent linkIds for all health parameter groups.
 *
 * For each Observation question with a healthParameter that has an anchorLinkId,
 * finds all child questions (via `for` property) that are NOT the anchor.
 */
export function buildHealthParameterDependencyMap(questionnaire: IQuestionnaireWithHealthParameters): {
  anchorToDependents: Map<string, string[]>
  dependentLinkIds: Set<string>
} {
  const anchorToDependents = new Map<string, string[]>()
  const dependentLinkIds = new Set<string>()

  for (const question of questionnaire.item) {
    if (question.type !== 'Observation' || !question.healthParameter) continue

    const hp = question.healthParameter
    if (!('anchorLinkId' in hp)) continue

    const generalHp = hp as HealthParameter
    if (!generalHp.anchorLinkId) continue

    const questionnaireSlug = question.questionnaireSlug
    if (!questionnaireSlug) continue

    const dependents = questionnaire.item
      .filter((q) => q.for === questionnaireSlug)
      .map((q) => q.linkId || q.id || '')
      .filter((id) => id && id !== generalHp.anchorLinkId)

    if (dependents.length > 0) {
      anchorToDependents.set(generalHp.anchorLinkId, dependents)
      for (const dep of dependents) {
        dependentLinkIds.add(dep)
      }
    }
  }

  return { anchorToDependents, dependentLinkIds }
}

/**
 * Get observations from a question's health parameter
 */
function getObservationsFromQuestion(
  question: QuestionnaireQuestionWithHealthParameters,
): HealthParameterObservation[] {
  if (question.healthParameter && 'observations' in question.healthParameter) {
    const hp = question.healthParameter as HealthParameter | undefined
    return hp?.observations || []
  }

  return []
}

/**
 * Adds health parameter observations to answers when an Observation question
 * is answered with 'edit' or 'keep'.
 *
 * Only processes Observation questions that have health parameters and valid answers.
 * Observations are only added if their linkIds exist in answerableQuestions.
 */

/**
 * Adds health parameter observations to answers immediately when 'keep' or 'edit' is selected.
 * This happens BEFORE skip logic, so observations are added even if questions will be skipped.
 *
 * Behavior:
 * - 'keep': ALWAYS overwrites observation values (even if they exist)
 * - 'edit': Only prefills if the observation answer doesn't exist yet
 */
export function addHealthParametersToAnswersOnAnswer(
  answers: { [linkId: string]: QuestionAnswerInput | undefined },
  questionnaire: IQuestionnaireWithHealthParameters,
  newlyAnsweredLinkId: string,
): { [linkId: string]: QuestionAnswerInput } {
  const result = { ...answers } as { [linkId: string]: QuestionAnswerInput }
  const allLinkIds = new Set(questionnaire.item.map((q) => q.linkId || q.id || '').filter(Boolean))

  // Only process the newly answered question
  const answer = result[newlyAnsweredLinkId]
  if (!answer) return result

  const question = questionnaire.item.find((q) => (q.linkId || q.id || '') === newlyAnsweredLinkId) as
    | QuestionnaireQuestionWithHealthParameters
    | undefined

  if (!question || question.type !== 'Observation' || isNil(question.healthParameter)) {
    return result
  }

  const answerValue = answer?.value
  if (typeof answerValue !== 'string' || (answerValue !== 'keep' && answerValue !== 'edit')) {
    return result
  }

  // Get observations and filter to only those that exist in the questionnaire
  const observations = getObservationsFromQuestion(question).filter((obs) => allLinkIds.has(obs.linkId))

  // Add observations to result
  for (const observation of observations) {
    const existingAnswer = result[observation.linkId]

    const shouldAddObservation = answerValue === 'keep' || (answerValue === 'edit' && !existingAnswer)

    if (shouldAddObservation) {
      // ALWAYS overwrite when 'keep' is selected
      // Only prefill when 'edit' is selected and answer doesn't exist yet
      result[observation.linkId] = {
        linkId: observation.linkId,
        value: observation.value,
        text:
          observation.text ??
          (typeof observation.value === 'string' ? observation.value : JSON.stringify(observation.value)),
      }
    }
  }

  return result
}

/**
 * Adds health parameter observations to answers for all Observation questions with 'keep' answer.
 * Used during initialization to ensure observations are synced with latest health parameter values.
 * ALWAYS overwrites observation values when 'keep' is selected to ensure consistency.
 */
export function addHealthParametersToAnswersOnInitialize(
  answers: { [linkId: string]: QuestionAnswerInput | undefined },
  questionnaire: IQuestionnaireWithHealthParameters,
): { [linkId: string]: QuestionAnswerInput } {
  const result = { ...answers } as { [linkId: string]: QuestionAnswerInput }
  const allLinkIds = new Set(questionnaire.item.map((q) => q.linkId || q.id || '').filter(Boolean))

  // Process all Observation questions with 'keep' answer
  for (const question of questionnaire.item) {
    const linkId = question.linkId || question.id || ''
    const answer = result[linkId]

    if (!answer || question.type !== 'Observation' || isNil(question.healthParameter)) {
      continue
    }

    const answerValue = answer?.value
    if (typeof answerValue !== 'string' || answerValue !== 'keep') {
      continue
    }

    // Get observations and filter to only those that exist in the questionnaire
    const observations = getObservationsFromQuestion(question as QuestionnaireQuestionWithHealthParameters).filter(
      (obs) => allLinkIds.has(obs.linkId),
    )

    // Add observations to result - ALWAYS overwrite when 'keep' is selected
    // This ensures that observations are synced with the latest health parameter values
    for (const observation of observations) {
      result[observation.linkId] = {
        linkId: observation.linkId,
        value: observation.value,
        text:
          observation.text ??
          (typeof observation.value === 'string' ? observation.value : JSON.stringify(observation.value)),
      }
    }
  }

  return result
}
